/**
 * Playing a person, on the board the famous games are played on.
 *
 * The board is `table.js`, shared with `vs-computer.html`. What is here is what is
 * particular to there being somebody else involved: a room code in the URL, a
 * websocket to `relay.mjs`, and the small courtesies two people need that a
 * machine does not — asking for a move back rather than simply taking it, offering
 * a rematch rather than starting one.
 *
 * THE SERVER OWNS THE GAME, AND THAT IS WHY THIS IS SIMPLE
 * A click here does not move anything. It sends the move to the relay, which
 * checks it against its own position and sends it back out to everybody in the
 * room — including whoever sent it — and only then is it played onto the board.
 * The round trip costs nothing on a local network and buys the one property worth
 * having: both browsers apply the same moves in the same order from the same
 * source, so there is no way for the two boards to drift apart and no
 * reconciliation to write. It also means a watcher runs exactly the same code as
 * a player, having simply never sent anything.
 *
 * ARRIVING LATE IS THE ONLY CASE
 * Joining a game in progress, coming back after a dropped connection, and opening
 * the link for the first time are all the same thing: the relay answers `join`
 * with the score so far, and `setScore` puts the board there. There is no separate
 * catch-up path, because a separate path is a second chance to be wrong.
 */

import { createTable } from './table.js';
import { uciOf } from './live-game.js';
import { drawQr } from './qr.js';
import { publicUrl } from '../public-url.js';
import { mountNav } from '../nav.mjs';
import { mountInstall } from '../install.js';
import { mountCameraMenu, CAMERA_WORDS } from '../camera-menu.js';
import { mountLeanLayout } from '../lean-layout.js';
import { count } from '../analytics.js';
import { note, drop } from '../resume.js';
import {
  annotateGame, getGame, newGameId, noteLevel, resultOf, saveGame, syncGames,
} from '../data/my-games.js';
import { noteUnrated, showLevel } from './level-line.js';
import { CONTROLS, formatClock, parseControl } from './clock.js';

const $ = (id) => document.getElementById(id);

// The sections, as a menu, into the slot the masthead holds for it. Before
// anything else this file does, and in particular before the wait for three.js:
// somebody who lands here and would rather be somewhere else should not have to
// wait for a WebGL context to find out where else there is.
mountNav($('nav-slot'), { here: 'play', base: '../' });
mountInstall($('install-slot'), { base: '../' });

/** Where `relay.mjs` is listening. Same origin, so this works wherever it is served. */
const RELAY = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/play/relay`;

/**
 * Whether the seat has been reported yet.
 *
 * `hello` arrives on every connection, and this page reconnects on purpose — a
 * dropped train tunnel is one seat, not four. Reported once per page, so what the
 * numbers say is how many people sat down rather than how good their wifi was.
 */
let seatCounted = false;

/** How long to wait before trying again, and how far that backs off. */
const RETRY_MS = 800;
const RETRY_MAX = 8000;

let socket = null;
let seat = null;            // 'w' | 'b' | null while watching
let peers = { w: false, b: false, watchers: 0 };
let over = null;
let retry = RETRY_MS;
let closedOnPurpose = false;

const room = roomCode();

/**
 * The key the room gave this page with its seat (`play/seats.js`). The room keeps
 * an empty seat for whoever holds it, so a dropped connection or a reload comes
 * back to the same chair instead of racing everyone else in the room for it.
 * Kept in localStorage, per room, so it survives a reload as well as a reconnect.
 */
const SEAT_KEY = `friend-seat-key:${room}`;
const seatKey = {
  get() { try { return localStorage.getItem(SEAT_KEY) || undefined; } catch { return undefined; } },
  set(key) { try { if (key) localStorage.setItem(SEAT_KEY, key); } catch { /* private window */ } },
};
/**
 * This room's current game in your games list (`../data/my-games.js`). Kept per
 * room, like the seat key, so a reload carries on the same game rather than
 * starting a second copy of it; a rematch is a new game and gets a new id.
 */
const GAME_KEY = `friend-game:${room}`;
let gameId = null;
let written = '';
function newGame() {
  gameId = newGameId('friend');
  written = '';
  try { localStorage.setItem(GAME_KEY, gameId); } catch { /* private window */ }
}
syncGames();

/** The time control this link carries, if any — sent when joining. */
const linkControl = new URLSearchParams(location.search).get('tc');
showRoom();


// ------------------------------------------------------------------- the room

/**
 * The room code, out of the URL or newly made.
 *
 * Written back into the address bar with `replaceState` rather than a redirect, so
 * the link in the box is the link in the browser's own bar — which is the one a
 * person will copy if they do not notice the box.
 */
function roomCode() {
  const asked = new URLSearchParams(location.search).get('room');
  if (asked && /^[a-z0-9]{6,24}$/i.test(asked)) return asked.toLowerCase();
  // No l, 1, o or 0: this gets read aloud and typed by hand often enough to be
  // worth losing four characters over.
  const alphabet = 'abcdefghijkmnpqrstuvwxyz23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(6));
  const made = [...bytes].map((b) => alphabet[b % alphabet.length]).join('');
  const url = new URL(location.href);
  url.searchParams.set('room', made);
  history.replaceState(null, '', url);
  return made;
}

function showRoom() {
  const url = new URL(location.href);
  url.searchParams.set('room', room);
  const link = publicUrl(url);
  $('room-link').value = link;
  $('room-code').textContent = room;

  // The QR. `drawQr` returns null for something too long to fit in the versions
  // it knows, which a room link never is — but if it ever were, the card comes
  // off the page rather than showing a symbol that cannot be read.
  const drawn = drawQr($('qr'), link, { scale: 7 });
  $('qr-box').hidden = !drawn;

  // Share is only offered where it exists. On a phone this opens the sheet with
  // every messaging app on the device in it, which is exactly how a link like
  // this actually gets sent; on a desktop browser without it, Copy is the answer
  // and a button that did nothing would be worse than no button.
  const canShare = typeof navigator.share === 'function';
  $('share').hidden = !canShare;
  if (canShare) {
    $('share').addEventListener('click', async () => {
      try {
        await navigator.share({
          title: 'A game of chess',
          text: 'Your move — open this and we can play.',
          url: link,
        });
      } catch (err) {
        // Cancelling the sheet throws `AbortError`, which is not a failure and
        // must not be reported as one.
        if (err?.name !== 'AbortError') table.say('That could not be shared.');
      }
    });
  }
}

$('copy').addEventListener('click', async () => {
  const box = $('room-link');
  try {
    await navigator.clipboard.writeText(box.value);
    $('copy').textContent = 'Copied';
    setTimeout(() => { $('copy').textContent = 'Copy'; }, 1400);
  } catch {
    // Clipboard permission refused, or an insecure origin. Selecting the text is
    // the fallback every browser allows, and says what to do next by itself.
    box.select();
  }
});

$('new-room').addEventListener('click', () => {
  const url = new URL(location.href);
  url.searchParams.delete('room');
  location.href = url.toString();
});

// Their code, typed in: the same room a link would have opened. Spaces and a
// pasted whole link are forgiven, since both are what people actually enter.
$('join').addEventListener('submit', (e) => {
  e.preventDefault();
  const typed = $('join-code').value.trim();
  const code = (typed.match(/[?&]room=([a-z0-9]+)/i)?.[1] ?? typed.replace(/\s+/g, '')).toLowerCase();
  const error = $('join-error');
  if (!/^[a-z0-9]{6,24}$/.test(code)) {
    error.textContent = 'A room code is six letters and numbers.';
    error.hidden = false;
    return;
  }
  if (code === room) {
    error.textContent = 'That is this room. Send it to them instead.';
    error.hidden = false;
    return;
  }
  const url = new URL(location.href);
  url.searchParams.set('room', code);
  location.href = url.toString();
});
$('join-code').addEventListener('input', () => { $('join-error').hidden = true; });

// The board, only now. Everything above is the room and the link to send,
// which is page rather than scene: it is on screen while three.js loads, since
// there is no game to play until somebody opens the link anyway.
const table = await createTable();
// The six angles as one button over the board. See `../camera-menu.js`.
mountCameraMenu($('views'), { labels: CAMERA_WORDS });
// 2D / 3D to the masthead and full screen to the bar (`../lean-layout.js`).
mountLeanLayout();
const live = table.live;

table.canPick = () => Boolean(seat) && !over && live.turn === seat && peers.w && peers.b;
table.onPick = (mv) => send({ t: 'move', uci: uciOf(mv) });

connect();

// -------------------------------------------------------------- the connection

function connect() {
  closedOnPurpose = false;
  table.say('');
  // The room is in the URL as well as in the `join` message that follows. The
  // node relay reads the message and ignores the query; the Cloudflare one cannot
  // — it has to pick WHICH room object to hand the socket to before there is a
  // socket to receive a message on. So the code goes in both places, and the two
  // servers are the same protocol.
  socket = new WebSocket(`${RELAY}?room=${encodeURIComponent(room)}`);

  socket.addEventListener('open', () => {
    retry = RETRY_MS;
    // The seat is a preference, not a claim: the relay gives it only if it is
    // free, which is what makes coming back after a drop land in the same chair.
    send({ t: 'join', room, seat: seat || undefined, key: seatKey.get(), control: linkControl ?? undefined });
  });

  socket.addEventListener('message', (event) => {
    let message;
    try {
      message = JSON.parse(event.data);
    } catch {
      return;
    }
    hear(message);
  });

  socket.addEventListener('close', () => {
    if (closedOnPurpose) return;
    table.say(`Connection lost — trying again in ${Math.round(retry / 1000)}s`);
    reflect();
    setTimeout(connect, retry);
    retry = Math.min(retry * 2, RETRY_MAX);
  });

  // `error` is always followed by `close`, so the retry lives there and this only
  // has to avoid the unhandled-rejection noise in the console.
  socket.addEventListener('error', () => {});
}

function send(message) {
  if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
}

/** Everything the relay says. */
async function hear(message) {
  // Every message that changes the game carries the room's clock with it.
  if ('clock' in message) showClock(message.clock);
  switch (message.t) {
    case 'hello': {
      seat = message.seat;
      seatKey.set(message.key);
      if (!seatCounted) {
        seatCounted = true;
        count('friend-seat', seat === 'w' ? 'white' : seat === 'b' ? 'black' : 'watching');
      }
      peers = message.peers;
      over = message.over;
      if (seat) {
        let kept = null;
        try { kept = localStorage.getItem(GAME_KEY); } catch { /* private window */ }
        const had = kept && getGame(kept);
        // The game this page was last writing, unless that one ended and the
        // room has a game going now — then it is a later game in the same room.
        if (kept && !(had?.over && !over)) { gameId = kept; written = ''; } else newGame();
        // Which game in your list this is, so a rated result lands on it. See
        // `referee.js`: the room knows who you are from your sign-in, not from this.
        send({ t: 'mine', game: gameId });
      }
      table.you = seat || 'w';
      table.state = 'playing';
      table.opponentName = seat ? 'Your opponent' : 'Black';
      // The score so far — which is nothing at all on a new room, the whole game
      // when watching, and everything up to the drop when coming back.
      await table.setScore(message.moves);
      await table.faceYourSide(true);
      $('moves').hidden = false;
      if (over) showOver();
      reflect();
      return;
    }
    case 'peer': {
      peers = message.peers;
      reflect();
      return;
    }
    case 'replaced': {
      // This seat was taken back by the same key: the game is open in another
      // tab. Reconnecting would take it back again, and the two tabs would pass
      // the seat between them forever, so this one stops.
      closedOnPurpose = true;
      seat = null;
      table.say('This game is open in another tab or window. Reload to play here instead.');
      reflect();
      return;
    }
    case 'move': {
      over = message.over;
      await table.applyUci(message.uci);
      // The relay only knows about mate and stalemate; the board also knows about
      // repetition, the fifty-move rule and a position nobody can mate from.
      if (!over && live.outcome) over = { by: live.outcome.by, winner: live.outcome.winner };
      hideOffer();
      if (over) showOver();
      reflect();
      return;
    }
    case 'over': {
      over = message.over;
      showOver();
      reflect();
      return;
    }
    case 'rewound': {
      over = null;
      hideOffer();
      $('over').hidden = true;
      table.explainMate(false);
      await table.setScore(message.moves);
      table.say('A move was taken back.');
      reflect();
      return;
    }
    case 'takeback': {
      offer(
        `${them()} would like their move back.`,
        [
          ['Let them', () => send({ t: 'takeback-reply', ok: true })],
          ['No', () => send({ t: 'takeback-reply', ok: false })],
        ],
      );
      return;
    }
    case 'takeback-reply': {
      if (!message.ok) table.say(`${them()} would rather you did not.`);
      return;
    }
    case 'rematch': {
      offer(`${them()} would like another game.`, [
        ['Play again', () => send({ t: 'rematch' })],
      ]);
      return;
    }
    case 'restart': {
      seat = message.seat;
      newGame();
      if (seat) send({ t: 'mine', game: gameId });
      peers = message.peers;
      over = null;
      hideOffer();
      $('over').hidden = true;
      table.explainMate(false);
      table.you = seat || 'w';
      await table.setScore([]);
      await table.faceYourSide();
      table.say('A new game — you have changed colours.');
      reflect();
      return;
    }
    case 'control': {
      reflect();
      return;
    }
    case 'level': {
      // What the room's rating of the game that just ended did to your level —
      // or why it was not rated. See `referee.js`.
      if (!seat || !gameId) return;
      if (message.unrated) noteUnrated($('over-why'), gameId, message.unrated);
      else noteLevel(message);
      if (message.opponent) annotateGame(gameId, { opponent: message.opponent });
      return;
    }
    case 'error': {
      table.say(message.why);
      return;
    }
    default:
  }
}

// -------------------------------------------------------------------- the game

$('take-back').addEventListener('click', () => {
  if (!seat || !live.ply) return;
  send({ t: 'takeback' });
  table.say(`Asked ${them()} for your move back.`);
});

$('resign').addEventListener('click', () => {
  if (!seat || over) return;
  if (confirm('Resign this game?')) send({ t: 'resign' });
});

addEventListener('keydown', (event) => {
  // Ctrl+Z is the browser's undo, not a request to take a move back.
  if (event.ctrlKey || event.metaKey || event.altKey) return;
  if (event.target.matches('input, textarea')) return;
  if (event.key === 'z' || event.key === 'Z') $('take-back').click();
});

/**
 * This room, for the homepage's "pick up where you left off" — see
 * `chess-app/resume.js`. Here in `reflect` because every message that changes
 * the game ends by calling it.
 *
 * Only for somebody SEATED: a watcher has no game to go back to, and a watcher
 * writing this would replace the game of a person who had one. Dropped when the
 * game ends, which includes a resignation and a flag as well as mate.
 */
function remember() {
  if (!seat) return;
  if (over) drop('friend');
  else note('friend', { room, moves: live.ply });
  record();
}

/**
 * This game, into your games. Only when it has changed, since `reflect` runs on
 * every message the room sends and most of them are not moves.
 */
function record() {
  if (!gameId) return;
  const now = `${live.ply}|${over ? over.by : ''}`;
  if (now === written) return;
  written = now;
  // Your friend by name, once a rated game has told us it (`annotateGame`).
  const friendName = getGame(gameId)?.opponent || 'Your friend';
  saveGame({
    id: gameId,
    kind: 'friend',
    room,
    you: seat,
    white: seat === 'w' ? 'You' : friendName,
    black: seat === 'b' ? 'You' : friendName,
    moves: live.game.moves.slice(0, live.ply),
    result: over ? resultOf(over.winner) : '*',
    by: over?.by || '',
    over: Boolean(over),
  });
}

function showOver() {
  table.state = 'over';
  table.putDown();
  if (over.by === 'checkmate') table.explainMate(true);
  const won = seat && over.winner === seat;
  const drew = !over.winner;
  $('over-line').textContent = drew ? 'Drawn'
    : seat ? (won ? 'You won' : `${them()} won`)
      : `${over.winner === 'w' ? 'White' : 'Black'} won`;
  $('over-why').textContent = over.by === 'timeout'
    ? (drew ? 'Out of time — but the other side could not have mated, so it is a draw.' : 'Out of time.')
    : `${capitalise(over.by)}.`;
  if (seat && gameId) {
    // Written first, so the line below finds this game finished in your list.
    record();
    showLevel($('over-why'), gameId);
  }
  const acts = $('over-acts');
  acts.innerHTML = '';
  if (seat) {
    act(acts, 'Offer a rematch', () => {
      send({ t: 'rematch' });
      table.say('Rematch offered.');
    });
  }
  $('over').hidden = false;
}

/** A question from the other side, with the two answers to it. */
function offer(text, choices) {
  $('offer-line').textContent = text;
  const acts = $('offer-acts');
  acts.innerHTML = '';
  for (const [label, onClick] of choices) {
    act(acts, label, () => { hideOffer(); onClick(); });
  }
  $('offer').hidden = false;
}

function hideOffer() { $('offer').hidden = true; }

// ------------------------------------------------------------- what it all says

function reflect() {
  const waiting = !peers.w || !peers.b;
  const connected = socket?.readyState === WebSocket.OPEN;
  remember();

  table.setPills(seat
    ? [them(), seat === 'w' ? 'You are White' : 'You are Black']
    : ['Watching', `${peers.watchers} watching`]);

  table.setTag(
    !connected ? 'offline'
      : over ? 'game over'
        : waiting ? 'waiting'
          : !seat ? 'watching'
            : live.turn === seat ? 'your move' : 'their move',
    !connected || waiting,
  );

  table.setLead(
    !connected ? 'Not connected'
      : over ? 'The game is over'
        : waiting ? 'Waiting for somebody to open the link'
          : !seat ? 'Two people are playing; you are watching'
            : live.turn === seat ? 'Click one of your men, then where it goes'
              : `${them()} is thinking`,
  );

  // The link to send is over the page until there is somebody to play: without
  // them there is no game. A watcher, with both seats taken, never sees it.
  $('room').hidden = connected && !waiting;
  $('room-note').textContent = !connected
    ? 'Not connected to the relay. This page needs the dev server running.'
    : seat === 'w' && waiting ? 'You have White. The game starts when they arrive.'
      : seat === 'b' && waiting ? 'You have Black. The game starts when they arrive.'
        : !seat ? 'Both seats are taken, so you are watching this one.'
          : 'They are here.';

  // The time control can be chosen by either player until the first move.
  $('clk-control').hidden = !seat || live.ply > 0 || Boolean(over);
  $('take-back').disabled = !seat || !live.ply || Boolean(over) || waiting;
  $('resign').disabled = !seat || Boolean(over) || waiting;
}

/** What to call the other player. There are no names here, so it is their colour. */
function them() {
  if (!seat) return 'Black';
  return seat === 'w' ? 'Black' : 'White';
}

function act(into, label, onClick) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'ctl';
  button.textContent = label;
  button.addEventListener('click', onClick);
  into.appendChild(button);
}

function capitalise(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

// ------------------------------------------------------------------ the clock

/**
 * The room's clock, as this page last heard it: time left for each side, who is
 * running, and when that was heard. The faces count down from there; the room
 * is still the only clock that decides anything.
 */
let clock = null;
let flagSent = false;

for (const c of CONTROLS) {
  const option = document.createElement('option');
  option.value = c.id;
  option.textContent = c.label;
  $('control').appendChild(option);
}
$('control').value = parseControl(linkControl)?.id ?? 'none';
$('control').addEventListener('change', () => {
  const id = $('control').value;
  send({ t: 'control', control: id });
  // The link carries the choice, so whoever opens it next joins with it.
  const url = new URL(location.href);
  if (id === 'none') url.searchParams.delete('tc'); else url.searchParams.set('tc', id);
  history.replaceState(null, '', url);
  $('room-link').value = publicUrl(url);
});

function showClock(state) {
  clock = state ? { ...state, heard: performance.now() } : null;
  flagSent = false;
  $('clk-faces').hidden = !clock;
  // Always show the room's real control. One the menu does not offer (a link can
  // carry any) gets an entry of its own: left showing the old choice, a player
  // would sit down to "10 + 5" and find they had three seconds.
  const value = clock?.control ?? 'none';
  const select = $('control');
  if (select.value !== value) {
    const control = parseControl(value);
    if (control && ![...select.options].some((o) => o.value === value)) {
      select.add(new Option(`${formatClock(control.base)} + ${control.inc / 1000} · from the link`, value));
    }
    select.value = value;
  }
  tick();
}

/** Time left for `side` right now, by the room's last word. */
function left(side) {
  if (!clock) return null;
  const since = clock.running === side ? performance.now() - clock.heard : 0;
  return clock.left[side] - since;
}

function tick() {
  if (!clock) return;
  for (const side of ['w', 'b']) {
    const ms = left(side);
    const face = $(`clock-${side}`);
    $(`clock-${side}-time`).textContent = formatClock(ms);
    face.classList.toggle('on', clock.running === side && !over);
    face.classList.toggle('low', ms < 10000 && ms > 0);
    face.classList.toggle('out', ms <= 0);
  }
  // Somebody's time is up and the room has not said so yet: ask it to look.
  if (!over && clock.running && left(clock.running) <= 0 && !flagSent) {
    flagSent = true;
    send({ t: 'flag' });
  }
}
setInterval(tick, 100);

reflect();

// Handy from devtools, and what `scripts/check-friend.mjs` drives the page through.
window.__friend = {
  live,
  room,
  get seat() { return seat; },
  get peers() { return peers; },
  get over() { return over; },
  get clock() { return clock ? { ...clock, w: left('w'), b: left('b') } : null; },
  setControl: (id) => { $('control').value = id; $('control').dispatchEvent(new Event('change')); },
  get connected() { return socket?.readyState === WebSocket.OPEN; },
  get stage() { return table.stage; },
  // The board's camera. Not the stage's: it is mounted on the canvas and outlives
  // the stages a resize builds over each new scene — see `table.js`.
  get camera() { return table.camera; },
  get engine() { return table.engine; },
  moveByUci: (text) => {
    const mv = live.fromUci(text);
    if (!mv) throw new Error(`${text} is not legal here`);
    table.onPick(mv);
  },
  click: (square) => table.click(square),
  takeBack: () => $('take-back').click(),
  send,
  uciOf,
};
